import { inject, Injectable, signal } from '@angular/core';
import { TableLazyLoadEvent } from 'primeng/table';
import { BaseHttpService } from './base.service';
import { ITableResponse } from '../model';

type PagedFilterEntry = { value: unknown; matchMode?: string };

/** Backend paged APIs expect PrimeNG-style filters: `{ field: { value } }`. */
function normalizePagedFilters(
  filters: Record<string, unknown>,
): Record<string, PagedFilterEntry> {
  const normalized: Record<string, PagedFilterEntry> = {};

  for (const [key, raw] of Object.entries(filters)) {
    if (raw === null || raw === undefined || raw === '') {
      continue;
    }

    const entry: PagedFilterEntry =
      typeof raw === 'object' &&
      raw !== null &&
      'value' in (raw as Record<string, unknown>)
        ? (raw as PagedFilterEntry)
        : { value: raw };

    const value = entry.value;
    if (value === null || value === undefined || value === '') {
      continue;
    }

    normalized[key] = entry;
  }

    return normalized;
}

/** UI filters override defaults; empty UI values fall back to defaultFilter. */
function mergePagedFilters(
  defaults: Record<string, unknown>,
  current: Record<string, unknown>,
): Record<string, PagedFilterEntry> {
  const merged = normalizePagedFilters({
    ...defaults,
    ...current,
  });

  const defaultNormalized = normalizePagedFilters(defaults);
  for (const [key, value] of Object.entries(defaultNormalized)) {
    if (!(key in merged)) {
      merged[key] = value;
    }
  }

  return merged;
}

@Injectable()
export class GetTableDataService<T> {
  $baseHttp = inject(BaseHttpService);

  dataSet = signal<T[]>(this.setLoading());

  totalRecords = signal(0);

  first = signal(0);

  rows = signal(10);

  defaultFilter = signal<{ [key: string]: any }>({});

  isLoading = signal<boolean>(true);

  URL = signal<string>('');

  tableMetadata = signal({} as TableLazyLoadEvent);

  resetTable() {
    this.first.set(0);
    this.tableMetadata.update((el) => {
      el.rows = this.rows();
      el.first = 0;
      el.filters = {};
      return el;
    });
    this.loadData(this.tableMetadata());
  }

  loadData($event?: TableLazyLoadEvent) {
    this.dataSet.set(this.setLoading());
    if (!$event) {
      this.tableMetadata.update((el) => {
        el.first = this.first();
        return el;
      });
    }

    this.isLoading.set(true);

    this.tableMetadata.set(Object.assign(this.tableMetadata(), $event));

    this.tableMetadata.update((el: any) => {
      const currentFilters =
        typeof el.filters === 'string'
          ? JSON.parse(el.filters)
          : el.filters || {};
      const mergedFilters = mergePagedFilters(
        this.defaultFilter(),
        currentFilters,
      );
      el.filters = JSON.stringify(mergedFilters);
      return { ...el };
    });
    this.getFromServer();
  }

  getFromServer() {
    const url = this.URL();
    if (!url) {
      this.dataSet.set([]);
      this.totalRecords.set(0);
      this.isLoading.set(false);
      return;
    }

    this.$baseHttp
      .post<ITableResponse<T>>(url, this.tableMetadata())
      .subscribe((res) => {
        this.dataSet.set(res.result?.items ?? []);
        this.totalRecords.set(res.result?.total ?? 0);
        this.isLoading.set(false);
      });
  }

  setLoading() {
    return Array.from({ length: 10 }).map((_, i) => i as T);
  }
}
